import { atomFamily } from "jotai/utils";
import { atomWithQuery } from "jotai-tanstack-query";
import { type TestTemplate } from "../utils/types";
import {
  and,
  collection,
  getDocs,
  or,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { customFirestore } from "../firebase";
import { getGptDriverInput, getPromptStepsText } from "../utils/helpers";
import { kAllTestsId } from "../routes/tests-home/tests-tree-view";
import { companyDataAtom } from "../routes/test-run/test-run-atoms";

interface TestListData {
  values: TestTemplate[];
  availableTags: string[];
}

// Builds the Firestore query backing the tests grid for a given org/folder.
// Shared so a live onSnapshot listener can match the one-time getDocs below
// exactly (org dual-field, optional folder filter, title ordering).
const buildTestsListQuery = (organizationId: string, folderId?: string) => {
  let queryConstraints = [
    or(
      where("organisationId", "==", organizationId),
      where("organizationId", "==", organizationId)
    ),
  ];
  if (folderId != null && folderId != kAllTestsId) {
    queryConstraints = [
      and(
        where("folderId", "==", folderId),
        or(
          where("organisationId", "==", organizationId),
          where("organizationId", "==", organizationId)
        )
      ),
    ];
  }
  // NOTE: the two where() clauses above intentionally cover both the British
  // ("organisationId") and American ("organizationId") field spellings.
  return query(
    collection(customFirestore, "custom-tests"),
    ...queryConstraints,
    orderBy("title", "asc")
  );
};

const getTestsAtom = atomFamily(
  ({
    organizationId,
    folderId,
  }: {
    organizationId: string;
    folderId?: string;
  }) =>
    atomWithQuery((get) => {
      const companyAtomValue = get(companyDataAtom(organizationId));

      return {
        queryKey: ["getTests", organizationId, folderId],
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: companyAtomValue.data != null,
        queryFn: async ({ queryKey: [, id, folderId] }) => {
          const testsListQuery = buildTestsListQuery(
            id as string,
            folderId as string | undefined
          );

          const testsListQuerySnapshots = await getDocs(testsListQuery);

          if (testsListQuerySnapshots.empty) {
            return { values: [], availableTags: [] };
          }
          const settingsData = companyAtomValue.data!.settings;
          const availableTags = settingsData?.tags ?? [];

          const values: TestTemplate[] = testsListQuerySnapshots.docs.map(
            (test) => {
              const testData = test.data() as TestTemplate;
              testData.id = test.id;
              testData.runHistory = testData.runHistory ?? [];
              testData.parsedCommands = getPromptStepsText(
                getGptDriverInput(testData.commands)
              );

              return testData;
            }
          );

          return { values, availableTags };
        },
      };
    }),
  (a, b) => a.folderId == b.folderId && a.organizationId == b.organizationId
);

export { getTestsAtom, buildTestsListQuery, type TestListData };
